import {
  Cable,
  TextCursorInputIcon,
  BotIcon,
  ImageIcon,
  SearchIcon,
  AudioLinesIcon,
  BotMessageSquareIcon,
  GlobeIcon,
  SplitIcon,
  Volume2Icon,
  VideoIcon,
  TvMinimalPlay,
  LogIn,
  LogOut,
  WrenchIcon,
  BrainIcon,
  ChevronsLeftRightEllipsis,
  TextIcon,
  CogIcon,
  FileChartColumn,
  FileUp,
  Braces,
  GalleryVerticalEnd,
  Edit,
  StickyNote,
  Database,
  DatabaseZap,
  Layers,
  Layers2,
  LayoutDashboard,
  BookOpen,
  FileBarChart,
  GalleryThumbnails,
  Code,
  FileCode2,
  ListChecks,
  ListPlus,
  ListTree,
  Sigma,
  WorkflowIcon,
  ChartLine,
  ChartNoAxesCombined,
  ChartColumn,
  SlidersHorizontal,
  ShieldCheck,
  OctagonPause,
  Shapes,
  FileSpreadsheet,
  FileText,
  Presentation,
  FolderClosed,
  FolderKanban,
  Network,
  TerminalSquare,
  Clapperboard,
  GitBranch,
  BadgeCheck,
  Repeat,
  Bell,
  Rocket,
  Tag,
  Camera,
  CloudDownload,
  MonitorCheck,
  DraftingCompass,
  PackageCheck,
  FilePen,
  Projector,
  Snowflake,
  Ticket,
  Landmark,
  Radio,
} from 'lucide-react';

export type IconName =
  | 'Cable'
  | 'TextCursorInputIcon'
  | 'BotIcon'
  | 'ImageIcon'
  | 'SearchIcon'
  | 'AudioLinesIcon'
  | 'BotMessageSquareIcon'
  | 'GlobeIcon'
  | 'SplitIcon'
  | 'Volume2Icon'
  | 'VideoIcon'
  | 'TvMinimalPlay'
  | 'LogIn'
  | 'LogOut'
  | 'WrenchIcon'
  | 'BrainIcon'
  | 'ChevronsLeftRightEllipsis'
  | 'TextIcon'
  | 'CogIcon'
  | 'FileChartColumn'
  | 'FileUp'
  | 'Braces'
  | 'GalleryVerticalEnd'
  | 'Edit'
  | 'StickyNote'
  | 'Database'
  | 'DatabaseZap'
  | 'Layers'
  | 'Layers2'
  | 'LayoutDashboard'
  | 'BookOpen'
  | 'FileBarChart'
  | 'GalleryThumbnails'
  | 'Code'
  | 'FileCode2'
  | 'FileCode2Icon'
  | 'ListChecks'
  | 'ListPlus'
  | 'ListTree'
  | 'Sigma'
  | 'WorkflowIcon'
  | 'ChartLine'
  | 'ChartNoAxesCombined'
  | 'SlidersHorizontal'
  | 'ChartColumn'
  | 'ShieldCheck'
  | 'OctagonPause'
  | 'Shapes'
  | 'FileSpreadsheet'
  | 'FileText'
  | 'Presentation'
  | 'FolderClosed'
  | 'FolderKanban'
  | 'Network'
  | 'NetworkIcon'
  | 'TerminalSquareIcon'
  | 'Clapperboard'
  | 'GitBranch'
  | 'BadgeCheck'
  | 'Repeat'
  | 'Bell'
  | 'BellIcon'
  | 'Rocket'
  | 'RocketIcon'
  | 'Tag'
  | 'TagIcon'
  | 'Camera'
  | 'CloudDownload'
  | 'MonitorCheck'
  | 'MonitorCheckIcon'
  | 'DraftingCompass'
  | 'PackageCheck'
  | 'FilePen'
  | 'Projector'
  | 'Snowflake'
  | 'Ticket'
  | 'Landmark'
  | 'Radio'
  | 'RadioIcon'
  | 'BracesIcon';

export const iconMap: Record<
  IconName,
  React.ComponentType<{ className?: string; style?: React.CSSProperties }>
> = {
  Cable,
  TextCursorInputIcon,
  BotIcon,
  ImageIcon,
  SearchIcon,
  AudioLinesIcon,
  BotMessageSquareIcon,
  GlobeIcon,
  SplitIcon,
  Volume2Icon,
  VideoIcon,
  TvMinimalPlay,
  LogIn,
  LogOut,
  WrenchIcon,
  BrainIcon,
  ChevronsLeftRightEllipsis,
  TextIcon,
  CogIcon,
  FileChartColumn,
  FileUp,
  Braces,
  GalleryVerticalEnd,
  Edit,
  StickyNote,
  Database,
  DatabaseZap,
  Layers,
  Layers2,
  LayoutDashboard,
  BookOpen,
  FileBarChart,
  GalleryThumbnails,
  Code,
  FileCode2,
  FileCode2Icon: FileCode2,
  ListChecks,
  ListPlus,
  ListTree,
  Sigma,
  WorkflowIcon,
  ChartLine,
  ChartNoAxesCombined,
  SlidersHorizontal,
  ChartColumn,
  ShieldCheck,
  OctagonPause,
  Shapes,
  FileSpreadsheet,
  FileText,
  Presentation,
  FolderClosed,
  FolderKanban,
  Network,
  NetworkIcon: Network,
  TerminalSquareIcon: TerminalSquare,
  Clapperboard,
  GitBranch,
  BadgeCheck,
  Repeat,
  Bell,
  BellIcon: Bell,
  Rocket,
  RocketIcon: Rocket,
  Tag,
  TagIcon: Tag,
  Camera,
  CloudDownload,
  MonitorCheck,
  MonitorCheckIcon: MonitorCheck,
  DraftingCompass,
  PackageCheck,
  FilePen,
  Projector,
  Snowflake,
  Ticket,
  Landmark,
  Radio,
  RadioIcon: Radio,
  BracesIcon: Braces,
};

export const getIconComponent = (
  iconName: IconName | string,
  color?: string,
  className: string = 'w-4 h-4',
): React.ReactNode => {
  const IconComponent = iconMap[iconName as IconName];
  if (!IconComponent) {
    // console.warn(`Icon "${iconName}" not found in icon mapping`);
    return null;
  }
  // If color is provided, use inline style. Otherwise, use theme-aware Tailwind classes
  const defaultColorClass = 'text-black dark:text-white';
  // Only add default color classes if className doesn't already contain text-* color classes
  const hasTextColorClass = /text-\w+/.test(className);
  const combinedClassName = color
    ? className
    : hasTextColorClass
      ? className
      : `${className} ${defaultColorClass}`;

  return (
    <IconComponent
      className={combinedClassName}
      style={color ? { color: color } : undefined}
    />
  );
};
